import { ApiFetch } from '../../../shared/utils/fetch'

/**
 * PE-2087 — a product's CURRENT many-to-many taxonomy links.
 *
 * These are not on the product row, so the edit form has to read them separately from the
 * three osprey-api-owned routes below (each proxies product-service):
 *   GET /products/:id/collections   GET /products/:id/categories   GET /products/:id/occasions
 *
 * ⚠️ Each answers with a BARE ARRAY of the linked rows — exactly like `/categories`,
 * `/collections` and `/occasions` themselves — not the `{ data: [...] }` envelope the
 * swagger examples in this repo imply. Verified live 2026-09-22 (all three returned []).
 * The reader below accepts both shapes so a later backend envelope can't flip the bug.
 *
 * The ids returned here are PRODUCT-SERVICE ids, the same namespace the pickers select
 * from — and a different namespace from the product's scalar `categoryId`.
 */
export interface ProductTaxonomyLinks {
  collectionIds: string[]
  categoryIds: string[]
  occasionIds: string[]
}

interface TaxonomyRow {
  id?: string
}

const idsOf = (res: unknown): string[] => {
  const rows: unknown[] = Array.isArray(res)
    ? res
    : ((res as { data?: unknown } | null | undefined)?.data as unknown[] | undefined) ?? []
  return rows.map(row => (row as TaxonomyRow)?.id).filter((id): id is string => !!id)
}

/**
 * Read all three link sets. Legs are settled independently: a single upstream 502 leaves
 * that one picker empty rather than blanking the whole prefill.
 */
export const fetchProductTaxonomy = async (productId: string): Promise<ProductTaxonomyLinks> => {
  const [collections, categories, occasions] = await Promise.allSettled([
    ApiFetch.get<unknown>(`/products/${productId}/collections`),
    ApiFetch.get<unknown>(`/products/${productId}/categories`),
    ApiFetch.get<unknown>(`/products/${productId}/occasions`),
  ])

  const settled = (result: PromiseSettledResult<unknown>): string[] =>
    result.status === 'fulfilled' ? idsOf(result.value) : []

  return {
    collectionIds: settled(collections),
    categoryIds: settled(categories),
    occasionIds: settled(occasions),
  }
}
